import type { CheckoutRules } from './checkout_env.js';
import type {
  CheckoutField,
  CheckoutItemSource,
  CheckoutMoney,
  CheckoutProductType,
} from './checkout_source.js';
import { productContract, type CheckoutAllocationOwner } from './product_contract.js';
import { sumItems } from './sum_items.js';

/** A oferta como o card 1-Click e a tela cheia a leem: os dois derivam daqui
 *  o manifesto, a política, o dinheiro, a data do saldo e o dono do rateio, em
 *  vez de cada um refazer a conta. */
export interface CheckoutOfferModel<I extends CheckoutItemSource = CheckoutItemSource> {
  /** O item que o envio ecoa (o 1º; no multi-trecho, a ida). */
  item: I;
  items: I[];
  productType: CheckoutProductType;
  /** Agregada sobre TODOS os itens: um multi-trecho só é dentro da política se
   *  cada trecho estiver dentro. */
  withinPolicy: boolean;
  fields: CheckoutField[];
  /** A soma dos itens: por assento no voo (multi-trecho soma as pernas). */
  perSeat: CheckoutMoney;
  /** O total do pedido: `perSeat` × viajantes onde o preço escala. */
  total: CheckoutMoney;
  /** O valor de cada pessoa, só quando o total soma mais de uma. */
  perTraveler: CheckoutMoney | undefined;
  /** O valor que o rateio de UM bloco cobre — o que o saldo do centro de custo
   *  dele confere: o total inteiro com um dono só, a parte de cada um quando
   *  todo viajante aloca. */
  allocationShare: number;
  startDate: string | undefined;
  units: number | undefined;
  allocationOwner: CheckoutAllocationOwner;
}

interface CheckoutOfferModelInput<I extends CheckoutItemSource> {
  items: I[];
  travelerCount: number;
  /** A tela que abre a oferta pode trocar o dono do rateio do produto (a
   *  bancada sem roster usa o solicitante, `checkout_drawer.tsx`). */
  allocationOwner?: CheckoutAllocationOwner;
  /** The manifest rule of the wire contract (`CheckoutRules.offerFields`). */
  offerFields: CheckoutRules['offerFields'];
}

/** `undefined` sem itens: não há oferta para mostrar. Um checkout é de um
 *  produto só (o do 1º item): um item de outro produto, que nenhum produtor
 *  emite, fica fora do modelo como fica fora do card e da tela, para o total
 *  e a política valerem para o que se vê. */
export function checkoutOfferModel<I extends CheckoutItemSource>({
  items: cart,
  travelerCount,
  allocationOwner,
  offerFields,
}: CheckoutOfferModelInput<I>): CheckoutOfferModel<I> | undefined {
  const [item] = cart;
  if (!item) return undefined;
  const items = cart.filter((i) => i.productType === item.productType);
  // The rules read the item as the kit's own shape: the host's richer item fits it.
  const source: CheckoutItemSource = item;
  const contract = productContract(source);
  const withinPolicy = items.every((i) => i.detail.withinPolicy);
  const perSeat = sumItems(items);
  const people = Math.max(travelerCount, 1);
  const scaled = contract.scalesByTraveler && people > 1;
  const total = scaled ? { amount: perSeat.amount * people, currency: perSeat.currency } : perSeat;
  const owner = allocationOwner ?? contract.allocationOwner;
  return {
    item,
    items,
    productType: item.productType,
    withinPolicy,
    fields: offerFields(item.productType, withinPolicy),
    perSeat,
    total,
    perTraveler: scaled ? perSeat : undefined,
    allocationShare: owner === 'requester' ? total.amount : total.amount / people,
    startDate: contract.startDate(source),
    units: contract.units(source),
    allocationOwner: owner,
  };
}
